import {PriceRiseHintResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {FadeTransition} from '@aboutyou/transitions';
import {
  Button,
  COLORS,
  GenericIconName,
  Icon,
  Text,
  useTimedOut,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React, {useEffect} from 'react';
import sc from 'styled-components';
import {DeleteWithUndoProps} from './DeleteWithUndo';

interface DeleteWithUndoViewProps
  extends DeleteWithUndoProps,
    PriceRiseHintResponse {
  style?: React.CSSProperties;
}

export const DeleteWithUndoView: React.FC<DeleteWithUndoViewProps> = ({
  style,
  deleteItemWithDelay,
  onUndoClick,
  deleteItem,
  shouldCheckForPriceRise,
  type,
}) => {
  const {t} = useTranslation();
  const canShowContent = useTimedOut(800);

  useEffect(() => {
    if (type?.$case === 'empty' || !shouldCheckForPriceRise) {
      deleteItemWithDelay();
    }
  }, [type]);

  return (
    <Container data-testid="productRevertSection" style={style}>
      <StyledFadeTransition duration={250}>
        <ContentWrapper>
          {canShowContent ? (
            <>
              {!type || type.$case === 'empty' ? (
                <RegularCase data-testid="deleteRegularCase">
                  <Text color="White">{t('Product is being removed...')}</Text>
                  <EmptyButton
                    data-testid="revertButton"
                    onClick={onUndoClick}
                    aria-label={undefined}
                    variant="secondary"
                  >
                    <Icon asset={GenericIconName.Reset} size="S" />
                    {t('Cancel')}
                  </EmptyButton>
                </RegularCase>
              ) : (
                <PriceRiseAlert>
                  <PriceRiseAlertHeadline data-testid="deletePriceRiseAlert">
                    <Icon
                      asset={GenericIconName.Coupon}
                      size="SS"
                      fill="White"
                    />
                    <Text size="px12" transform="uppercase" color="White">
                      {type.alert.headline}
                    </Text>
                  </PriceRiseAlertHeadline>
                  <Text size="px12" color="White" textAlignment="center">
                    {type.alert.text}
                  </Text>
                  <ActionButtons>
                    <EmptyButton
                      data-testid="confirmDeletion"
                      onClick={deleteItem}
                      variant="secondary"
                    >
                      {type.alert.secondaryCta.label}
                    </EmptyButton>
                    <FilledButton
                      data-testid="revertButton"
                      onClick={onUndoClick}
                      aria-label={undefined}
                    >
                      <Icon
                        asset={GenericIconName.Coupon}
                        size="XS"
                        fill="Red"
                      />
                      {type.alert.primaryCta.label}
                    </FilledButton>
                  </ActionButtons>
                </PriceRiseAlert>
              )}
            </>
          ) : (
            <Icon asset={GenericIconName.Trash} size="M" fill="White" />
          )}
        </ContentWrapper>
      </StyledFadeTransition>
    </Container>
  );
};

const Container = sc.div`
  position: absolute;
  top: 0;
  background-color: ${COLORS.Red};
  width: 100%;
  height: 100%;
  z-index: 1;
  display: flex;
  align-items: stretch;
`;

const StyledFadeTransition = sc(FadeTransition)`
  display: flex;
  height: 100%;
`;

const ContentWrapper = sc.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const RegularCase = sc.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
`;

const EmptyButton = linariaStyled(Button)`  
  &&& {
    --border-color: ${COLORS['Text.White']};
    
    &&:hover,
    &&:active {
      color: ${COLORS['Text.Black']};
    }
  }
`;

const PriceRiseAlert = sc.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding-inline: 56px;

  > span {
    line-height: 1.5;
  }
`;

const PriceRiseAlertHeadline = sc.div`
  display: flex;
  align-items: center;
  padding-inline: 8px;
  gap: 8px;
`;

const ActionButtons = sc.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  gap: 8px;
`;

const FilledButton = linariaStyled(EmptyButton)`
  color: ${COLORS.Red};
  background-color: ${COLORS.White};
`;
